import { useState, useEffect } from 'react';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, User as UserIcon, Shield, Building2, FileText, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, value }) => (
  <div className="flex justify-between py-1.5 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm text-slate-900 font-medium text-right">{value ?? '—'}</span>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <Card className="p-5 border border-slate-200" data-testid={`detail-section-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-brand" />}{title}</h3>
    {children}
  </Card>
);

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/users/detail/${userId}`, { withCredentials: true });
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load user');
      } finally { setLoading(false); }
    })();
  }, [userId]);

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div></Layout>;
  if (error) return <Layout><div className="max-w-3xl mx-auto p-8 text-center text-slate-500">{error}</div></Layout>;

  const acc = data.account || {};
  const role = acc.role;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-user-detail">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="user-detail-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Admin
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-brand-light flex items-center justify-center">
            <UserIcon className="w-6 h-6 text-brand" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{acc.name || 'Unknown'}</h1>
            <p className="text-sm text-slate-500">{acc.email} · <span className="capitalize">{role}</span></p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="Account" icon={UserIcon}>
            <Row label="Role" value={role} />
            <Row label="Status" value={acc.is_active === false ? 'Inactive' : 'Active'} />
            <Row label="Trash state" value={acc.deleted_state || 'active'} />
            <Row label="Joined" value={acc.created_at ? new Date(acc.created_at).toLocaleDateString() : '—'} />
            <Row label="Profile visible" value={String(acc.profile_visible ?? '—')} />
          </Section>

          {(role === 'job_seeker' || !role) && (
            <>
              <Section title="Transition" icon={FileText}>
                <Row label="Resume complete" value={String(data.transition?.is_complete ?? '—')} />
                <Row label="Resume visible" value={String(data.transition?.visible ?? '—')} />
                <Row label="Home location" value={data.transition?.home_location ? `${data.transition.home_location.city || ''} ${data.transition.home_location.state || ''}`.trim() : '—'} />
                <Row label="Transition date" value={data.transition?.transition_date} />
                <Row label="Saved opportunities" value={data.saved_opportunities_count} />
                <Row label="Conversations" value={data.conversations_count} />
              </Section>

              <Section title="Military" icon={Shield}>
                {(data.military || []).length === 0 ? <p className="text-sm text-slate-400">No military roles on file.</p> : (
                  (data.military || []).map((m, i) => (
                    <div key={i} className="py-2 border-b border-slate-100 last:border-0">
                      <p className="text-sm font-medium text-slate-900">{[m.rank, m.branch].filter(Boolean).join(' · ') || 'Role'}</p>
                      <p className="text-xs text-slate-500">{[m.mos_code, sanitizeMosTitle(m.mos_title, m.rank)].filter(Boolean).join(' — ')}</p>
                      {m.base_snapshot?.name && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{m.base_snapshot.name}</p>}
                    </div>
                  ))
                )}
              </Section>

              <Section title="Applications" icon={FileText}>
                <Row label="Total" value={data.applications_summary?.total} />
                <Row label="In motion" value={data.applications_summary?.in_motion} />
                <Row label="Hired" value={data.applications_summary?.hired} />
              </Section>
            </>
          )}

          {role === 'employer' && (
            <Section title="Organizations & Jobs" icon={Building2}>
              {(data.organizations || []).map((o, i) => (
                <Row key={i} label={o.company_name || o.employer_id} value={o.status} />
              ))}
              <Row label="Jobs total" value={data.jobs_summary?.total} />
              <Row label="Jobs active" value={data.jobs_summary?.active} />
              <Row label="Applications to jobs" value={data.applications_to_jobs} />
            </Section>
          )}

          {role === 'regional_admin' && (
            <Section title="Region Assignment" icon={MapPin}>
              {data.assignment ? (
                <>
                  <Row label="Region" value={data.region?.name} />
                  <Row label="State" value={data.region?.state} />
                  <Row label="Assigned at" value={data.assignment.assigned_at ? new Date(data.assignment.assigned_at).toLocaleDateString() : '—'} />
                </>
              ) : <p className="text-sm text-amber-600 font-medium">Unassigned — no region linked.</p>}
            </Section>
          )}

          <Section title="Recent Activity" icon={FileText}>
            {(data.recent_activity || []).length === 0 ? <p className="text-sm text-slate-400">No recent product activity.</p> : (
              <div className="space-y-1.5">
                {(data.recent_activity || []).map((e) => (
                  <div key={e.event_id} className="flex justify-between text-sm">
                    <span className="text-slate-700">{e.event_name}</span>
                    <span className="text-slate-400 text-xs">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </Layout>
  );
}
